import { Link } from "react-router-dom";

/**
 * TriNexa AI logo lockup — fully scalable SVG
 *  - Circuit-eye icon (inspired by the official brand)
 *  - "TRINEXA AI" wordmark with subtle glow
 *  - "VISION BEYOND INTELLIGENCE" tagline
 *
 * Renders crisply at any size and blends perfectly with the dark navy theme.
 */
export default function Logo({ className = "", testId = "logo", variant = "navbar", showTagline }) {
  const sizes = {
    navbar: {
      icon: "w-11 h-11 md:w-12 md:h-12",
      brand: "text-xl md:text-[22px]",
      tag: "text-[8px] md:text-[9px]",
      gap: "gap-2.5",
      tagline: false,
    },
    footer: {
      icon: "w-12 h-12",
      brand: "text-2xl",
      tag: "text-[10px]",
      gap: "gap-3",
      tagline: true,
    },
    hero: {
      icon: "w-20 h-20 md:w-24 md:h-24",
      brand: "text-4xl md:text-5xl",
      tag: "text-xs md:text-sm",
      gap: "gap-5",
      tagline: true,
    },
  };
  const c = sizes[variant] || sizes.navbar;
  const tagline = showTagline !== undefined ? showTagline : c.tagline;

  return (
    <Link
      to="/"
      data-testid={testId}
      aria-label="TriNexa AI — Vision Beyond Intelligence"
      className={`group inline-flex items-center ${c.gap} ${className}`}
    >
      <LogoIcon className={c.icon} />
      <div className="flex flex-col leading-none">
        <span
          className={`font-display font-extrabold tracking-[0.08em] ${c.brand} text-white`}
          style={{
            textShadow: "0 0 20px rgba(0, 212, 255, 0.45)",
          }}
        >
          TRINEXA<span className="text-[#00D4FF]"> AI</span>
        </span>
        {tagline && (
          <span
            className={`font-mono-tech ${c.tag} text-slate-400 tracking-[0.35em] uppercase mt-1.5`}
          >
            Vision Beyond Intelligence
          </span>
        )}
      </div>
    </Link>
  );
}

function LogoIcon({ className = "" }) {
  return (
    <span className={`relative inline-block shrink-0 ${className}`} aria-hidden="true">
      <svg
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[2deg]"
      >
        <defs>
          {/* Outer glow */}
          <filter id="trnx-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="trnx-eye" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#7DF9FF" />
            <stop offset="55%" stopColor="#00D4FF" />
            <stop offset="100%" stopColor="#0EA5E9" />
          </linearGradient>
          <radialGradient id="trnx-pupil" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#7DF9FF" />
            <stop offset="40%" stopColor="#00D4FF" />
            <stop offset="100%" stopColor="#050B14" />
          </radialGradient>
          <linearGradient id="trnx-circuit" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7DF9FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>

        {/* Top circuit traces */}
        <g
          stroke="url(#trnx-circuit)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          filter="url(#trnx-glow)"
          opacity="0.95"
        >
          <path d="M60 8 V28" />
          <path d="M42 18 L42 28 L52 32" />
          <path d="M78 18 L78 28 L68 32" />
          <path d="M28 30 L36 30 L40 34" />
          <path d="M92 30 L84 30 L80 34" />
          {/* Bottom circuit traces */}
          <path d="M60 92 V112" />
          <path d="M42 102 L42 92 L52 88" />
          <path d="M78 102 L78 92 L68 88" />
          <path d="M28 90 L36 90 L40 86" />
          <path d="M92 90 L84 90 L80 86" />
        </g>

        {/* Circuit nodes */}
        <g fill="#00D4FF" filter="url(#trnx-glow)">
          <circle cx="60" cy="6" r="2.2" />
          <circle cx="42" cy="16" r="1.8" />
          <circle cx="78" cy="16" r="1.8" />
          <circle cx="26" cy="30" r="1.6" />
          <circle cx="94" cy="30" r="1.6" />
          <circle cx="60" cy="114" r="2.2" />
          <circle cx="42" cy="104" r="1.8" />
          <circle cx="78" cy="104" r="1.8" />
          <circle cx="26" cy="90" r="1.6" />
          <circle cx="94" cy="90" r="1.6" />
        </g>

        {/* Eye almond shape */}
        <g filter="url(#trnx-glow)">
          <path
            d="M14 60 C 30 38, 90 38, 106 60 C 90 82, 30 82, 14 60 Z"
            fill="none"
            stroke="url(#trnx-eye)"
            strokeWidth="3.5"
            strokeLinejoin="round"
          />
        </g>

        {/* Iris ring */}
        <circle
          cx="60"
          cy="60"
          r="18"
          fill="none"
          stroke="#7DF9FF"
          strokeWidth="2.2"
          opacity="0.9"
          filter="url(#trnx-glow)"
        />

        {/* Pupil */}
        <circle cx="60" cy="60" r="13" fill="url(#trnx-pupil)" />
        <circle cx="60" cy="60" r="6" fill="#050B14" />
        <circle cx="63" cy="57" r="2.2" fill="#7DF9FF" opacity="0.95" />
      </svg>
    </span>
  );
}
